/**
 * Documentación dentro de la web: /docs (índice) y /docs/<ruta>.md.
 *
 * Los .md de docs/ se incluyen al compilar (ver features/docs/catalogo.ts) y los ve cualquier
 * persona con sesión. El Markdown se convierte a elementos React, nunca a HTML crudo.
 */
import { useQuery } from '@tanstack/react-query';
import { META_CARGA_DE_PAGINA } from '../componentes/BarraCargaPagina';
import { useEffect, useMemo } from 'react';
import { Link, NavLink, useParams } from 'react-router';
import { CATALOGO, DOCUMENTO_INICIAL } from '../features/docs/catalogo';
import { markdownAElementos } from '../lib/markdown';
import './PaginaDocs.css';

export function PaginaDocs() {
  const parametro = useParams()['*'] ?? '';
  const ruta = parametro === '' ? DOCUMENTO_INICIAL : parametro;
  const documento = CATALOGO.find((d) => d.ruta === ruta);

  const texto = useQuery({
    queryKey: ['docs', ruta],
    queryFn: () => (documento ? documento.cargar() : Promise.resolve('')),
    enabled: Boolean(documento),
    staleTime: Infinity,
    meta: META_CARGA_DE_PAGINA,
  });

  const contenido = useMemo(
    () =>
      texto.data === undefined
        ? null
        : markdownAElementos(texto.data, {
            rutaActual: ruta,
            enlaceInterno: (destino, hijos, clave) => (
              <Link key={clave} to={destino}>
                {hijos}
              </Link>
            ),
          }),
    [texto.data, ruta],
  );

  // Al cambiar de documento se vuelve arriba, como en una página nueva.
  useEffect(() => {
    window.scrollTo?.(0, 0);
  }, [ruta]);

  const grupos = [...new Set(CATALOGO.map((d) => d.grupo))];

  return (
    <main className="pagina docs">
      <nav aria-label="Documentación" className="docs__menu">
        {grupos.map((grupo) => (
          <section key={grupo}>
            <h2>{grupo}</h2>
            <ul>
              {CATALOGO.filter((d) => d.grupo === grupo).map((d) => (
                <li key={d.ruta}>
                  <NavLink to={d.ruta === DOCUMENTO_INICIAL ? '/docs' : `/docs/${d.ruta}`} end>
                    {d.etiqueta}
                  </NavLink>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>

      <article className="docs__contenido" aria-live="polite">
        {!documento && (
          <>
            <h1>Documento no encontrado</h1>
            <p>
              No existe «{ruta}» en la documentación. <Link to="/docs">Volver al índice</Link>.
            </p>
          </>
        )}
        {documento && texto.isPending && <p className="vacio">Cargando…</p>}
        {documento && texto.isError && (
          <p role="alert">No se pudo cargar el documento. Recarga la página e intenta de nuevo.</p>
        )}
        {contenido}
      </article>
    </main>
  );
}
